import React, { useEffect, useCallback, useRef, useState, useMemo } from "react";
import { parseMasteryPacingToClassName, Skill, RootState, Student, WeeklyPlanStateData} from "@/models";
import { PlannerCurriculumLesson } from "@/features/planner/PlannerTypes";
import { AsyncLoadingState } from "@/features/dashboard/enums";
import { SkillType, MasteryStatus, MasteryPacing, MasteryTimeBooked, MasteryStatusPacing } from "@/enums";
import { getSkillTimeBalance } from "@/utilities";
import { getMondayFridayDates } from "@/features/dashboard/utils";
import { useRosterInfo } from "@/hooks";

// Store
import { useDispatch, useSelector } from "react-redux";
import {
  fetchStudentAssignments,
  fetchTeacherAssignments,
  getFetchAssignmentInputs,
  resetWeekState,
  selectWeeklyPlan,
  selectWeeklyPlanData,
  updateWeeklyPlanDates,
  WeeklyPlanState,
} from "@/store/slices";

const TEMPORARY_PINELLAS_DISTRICT_ID = 99999999999999999000000803;

export const useWeeklyPlan = () => {
  const dispatch = useDispatch();
  const weeklyPlan = useSelector(selectWeeklyPlan) as WeeklyPlanState;
  const weeklyPlanData = useSelector(selectWeeklyPlanData) as WeeklyPlanStateData;
  const {dateFrom, dateTo} = weeklyPlanData;
  const { userId, classroomId, students, reports, userData } = useRosterInfo() as { userId: string, classroomId: string, students: Student[], reports: any, userData: any };
  const { fetchTeacherAssignLoading, fetchStudentAssignLoading } = weeklyPlan;
  const prevClassroomIdRef = useRef<string>();
  const dispatchedRef = useRef(false);
  const { districtId } = userData;
  const [failedToLoad, setFailedToLoad] = useState(false);

  useEffect(() => {
    if (weeklyPlan.error) {
      setFailedToLoad(true);
    } else {
      setFailedToLoad(false);
    }
  }, [weeklyPlan.error]);

  // Adapt the starting date to the startDate of the academic calendar
  useEffect(() => {
    return; // Return early until we're ready to use this feature
    if (districtId && classroomId && districtId === TEMPORARY_PINELLAS_DISTRICT_ID ) {
      const calendarStartDate = new Date('2025-08-11'); // TODO: retrieve from academic calendar
      const [newDateFrom, newDateTo] = getMondayFridayDates( 0, new Date(calendarStartDate.setHours(24,0,0,0)));
      // Don't update if the dates are the same
      if (dateFrom !== newDateFrom && dateTo !== newDateTo) {
        dispatch(updateWeeklyPlanDates({ dateFrom: newDateFrom, dateTo: newDateTo }));
      }
    }
  }, [districtId, classroomId, dispatch, dateFrom, dateTo]);

  // initial page load of either the dashboard or lesson planner page
  useEffect(() => {
    if (!userId || !classroomId || !students || !students.length) return;
    const fetchInputs = getFetchAssignmentInputs({ weeklyPlan, reports, userData } as RootState);
    
    const hasClassChanged = prevClassroomIdRef.current !== undefined && prevClassroomIdRef.current !== classroomId;
    if (hasClassChanged) {
      dispatchedRef.current = false;
      dispatch(resetWeekState());
    }
    if (!prevClassroomIdRef.current) {
      prevClassroomIdRef.current = classroomId;
    }
        
    if (!fetchInputs || (weeklyPlanData?.assignments?.length && !hasClassChanged)) return;
    if (
      !dispatchedRef.current &&
      (fetchTeacherAssignLoading === AsyncLoadingState.NONE && fetchStudentAssignLoading === AsyncLoadingState.NONE)
    ) {
      handleFetchAssignments(fetchInputs);     
    }
    
    prevClassroomIdRef.current = classroomId;
  }, [
    dispatch,
    userId,
    classroomId,
    students,
    weeklyPlan,
    reports,
    userData,
    fetchTeacherAssignLoading,
    fetchStudentAssignLoading,
  ]);

  const reloadData = () =>{
    const fetchInputs = getFetchAssignmentInputs({ weeklyPlan, reports, userData } as RootState);
    if (!fetchInputs) return;
    dispatch(resetWeekState());
    handleFetchAssignments(fetchInputs);
  }

  const handleFetchAssignments = (fetchInputs: any) => {
    dispatch(fetchTeacherAssignments({ ...fetchInputs }) as any);
    dispatch(fetchStudentAssignments({ ...fetchInputs }) as any);
    dispatchedRef.current = true;
  };

  const getTimeAssigned = useCallback(
    (lessonPlanId: string, studentId: string, skillId: string, masteryGroup: string): number => {
      const lesson = weeklyPlanData.curriculum?.tracks
        ?.flatMap(({ units }) => units ?? [])
        .flatMap(({ lessons }) => lessons ?? [])
        .find((l) => l.lessonPlanId === lessonPlanId);
      return lesson?.timeAssigned?.[skillId]?.[masteryGroup]?.[studentId] ?? 0;
    },
    [weeklyPlanData]
  );

  const getLessonBookingStatus = useCallback(
    (lessonPlanId: string): Record<MasteryTimeBooked, Set<string>> => {
      const alertStatus = [
        MasteryTimeBooked.OVER_BOOKED,
        MasteryTimeBooked.UNDER_BOOKED,
        // MasteryTimeBooked.NO_DATA
      ]
      const initialReturn = {} as Record<MasteryTimeBooked, Set<string>>;
      if (
        !weeklyPlanData.curriculum 
        || !weeklyPlanData.mastery
        || !students
        || students.length === 0
      ) return initialReturn;
      const lessonMasteryRecords = weeklyPlanData.mastery[lessonPlanId] as
          { studentId: string, records: { skillId: string, pacing: MasteryStatus }[] }[] | undefined;
      const lesson: PlannerCurriculumLesson | undefined = (weeklyPlanData.curriculum.tracks ?? [])
        .flatMap(({units}) => units.flatMap(({lessons}) => lessons))
        .find(({lessonPlanId: lessonId}) => lessonId === lessonPlanId);
      // console.log(`-------======> Booking status:`, lessonPlanId, lesson, tracks)
      if (!lesson || !lessonMasteryRecords) return initialReturn;
      return students.reduce(
        (acc: Record<MasteryTimeBooked, Set<string>>, student) => {
          const studentMasteryRecord = lessonMasteryRecords.find(({studentId}) => studentId === student.id);
          for (const { skillId, timeToMaster } of lesson.skills.filter(({skillType}) => skillType === SkillType.FOCUS) ?? []) {
            if (!skillId || typeof timeToMaster !== 'number') continue;
            const pacingStandard = (studentMasteryRecord?.records.find(({skillId: sId}) => sId === skillId)?.pacing ?? MasteryPacing.NO_DATA) as MasteryPacing;
            const timeAssigned = lesson.timeAssigned?.[skillId ?? '']?.[parseMasteryPacingToClassName(pacingStandard)]?.[student.id] ?? 0;
            const masteryStatus: MasteryStatusPacing = pacingStandard === MasteryPacing.NO_DATA 
              ? MasteryStatusPacing.NO_DATA
                : pacingStandard === MasteryPacing.AHEAD_OF_GRADE_LEVEL
                  ? MasteryStatusPacing.LIKELY_MASTERED
                  : pacingStandard === MasteryPacing.AT_GRADE_LEVEL
                    ? MasteryStatusPacing.DEVELOPING
                      : pacingStandard === MasteryPacing.BELOW_GRADE_LEVEL
                        ? MasteryStatusPacing.NOT_DEVELOPED
                          : MasteryStatusPacing.DEVELOPING;
            const { skillBookingStatus } = getSkillTimeBalance( timeToMaster, timeAssigned, masteryStatus );
            // Add to the parent accumulator if we are tracking this status
            if (alertStatus.includes(skillBookingStatus)) {
              if (!acc[skillBookingStatus]) {
                acc[skillBookingStatus] = new Set<string>();
              }
              acc[skillBookingStatus].add(student.id);
            }
          }
          return acc;
        },
        {} as Record<MasteryTimeBooked, Set<string>>
      );
    },
    [weeklyPlanData, students, getTimeAssigned]
  );

  return {
    ...weeklyPlan,
    failedToLoad,
    getTimeAssigned,
    getLessonBookingStatus,
    reloadData,
  };
};
